// ** React Imports
import { useEffect, useRef, useState } from 'react';

import { client } from 'src/utils/axios-client';
import {
    LeadScreenStore,
    LeadActivity,
    Lead,
    NextActivity,
    LeadAccountStore,
    ViewType,
    ActivityDialogStore,
    LeadCompanyStore,
    CompanyDialogStore,
    LeadSprintStore,
    SprintDialogStore,
    LeadActivityHistoryStore,
    ActivityHistoryDialogStore,
    LeadActivityCadenceStore,
    CadenceListDialogStore,
    LeadActivityOutcomeStore,
    ActivityOutcomes,
    LeadActivityEmailStore,
    ActivityTemplateSelectionStore,
} from 'src/stores/LeadStore';
import { getActivityEndpoint, leadEndpoints } from '../../../configs/endpoints';

// ** MUI Imports
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import _ from 'lodash';
import Badge, { BadgeProps } from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Icon from 'src/features/common/CustomIcon';
import MenuItem from '@mui/material/MenuItem';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ListItemIcon from '@mui/material/ListItemIcon';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { ProblemDetails } from '../../../types/ApiErrorResponse';
import SaveTwoToneIcon from '@mui/icons-material/SaveTwoTone';
import FormControl from '@mui/material/FormControl';
import LocalPhoneTwoToneIcon from '@mui/icons-material/LocalPhoneTwoTone';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import DoActivityOutcomes from './DoActivityOutcomes';
import PhoneInTalkTwoToneIcon from '@mui/icons-material/PhoneInTalkTwoTone';
import SendIcon from '@mui/icons-material/Send';
import Alert from '@mui/material/Alert';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import StandardSkeleton from '../../common/StandardSkeleton';
import HtmlTextAreaView from '../../common2/Templating/HtmlTextAreaView';
import { EditorState } from 'draft-js';
import { useTokenizedContent } from '../../../hooks/useTokenizedContent';
import WysiwygEditor, { convertToHTML, TokenType } from '../../common2/Templating/WysiwygEditor';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import MenuIcon from '@mui/icons-material/Menu';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RenderIf, { ClaimType, ClaimValue } from '../../common/RenderIf';
import LeadActivityAttachments from './LeadActivityAttachments';
import ContentEditor from '../../../v2/components/ContentEditor/ContentEditor';

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: 33,
        top: 10,
        border: `2px solid ${theme.palette.background.paper}`,
        padding: '0 4px',
    },
}));

type StatusUpdate = { messageId: string; status: string };

const DoActivityOutboundEmail = () => {
    const outcomesRecord = LeadActivityOutcomeStore.useState((s) => s.record);
    const selectedLead = LeadScreenStore.useState((s) => s.selectedLead);
    const [emailFrom, setemailFrom] = useState(outcomesRecord.emailFrom);
    const [emailTo, setEmailTo] = useState(outcomesRecord.emailTo);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [smsBodyError, setSmsBodyError] = useState(false);
    const emailMessage = LeadActivityEmailStore.useState((s) => s.record);
    const [messageId, setMessageId] = useState('');
    const [sendButtonDisabled, setSendButtonDisabled] = useState(false);
    const [sentStatus, setSentStatus] = useState('');
    const [showMessageStatus, setShowMessageStatus] = useState(false);
    const [connection, setConnection] = useState<null | HubConnection>(null);
    const latestStatus = useRef({ messageId: '', status: '' } as StatusUpdate);
    const [status, setStatus] = useState<StatusUpdate>({ messageId: '', status: '' });
    const [defaultEmailTo, setDefaultEmailTo] = useState(outcomesRecord.emailTo);

    const [example1EditorState, setExample1EditorState] = useState('');
    const [showToolbar, setShowToolbar] = useState(false);
    const [withTokens, setWithTokens] = useState(true);
    const { tokenizedHtml } = useTokenizedContent(emailMessage.emailBody, emailMessage.leadId, TokenType.Lead, true);

    const [selected, setSelected] = useState(false);

    useEffect(() => {
        LeadActivityOutcomeStore.update((s) => {
            s.record.emailBody = example1EditorState;
        });
        LeadActivityEmailStore.update((s) => {
            s.record.emailBody = example1EditorState;
        });
    }, [example1EditorState]);

    const handleShowToolbarChange = (event: { target: { checked: boolean | ((prevState: boolean) => boolean) } }) => {
        setShowToolbar(event.target.checked);
    };

    useEffect(() => {
        setShowToolbar(selected);
    }, [selected]);

    useEffect(() => {
        const fetchOutcomes = async () => {
            const response = await client.get(
                leadEndpoints.getactivityoutcomes.replace('{leadid}', '' + selectedLead?.id.toString()),
            );
            LeadActivityOutcomeStore.update((s) => {
                s.record = response.data;
            });
            setemailFrom(response.data.emailFrom);
            setEmailTo(response.data.emailTo);
            setDefaultEmailTo(response.data.emailTo);

            LeadActivityEmailStore.update((s) => {
                s.record.leadId = response.data.leadId;
            });
            LeadActivityEmailStore.update((s) => {
                s.record.emailBody = response.data.emailBody;
            });
            LeadActivityEmailStore.update((s) => {
                s.record.emailTo = response.data.emailTo;
            });
            LeadActivityEmailStore.update((s) => {
                s.record.emailFrom = response.data.emailFrom;
            });
            LeadActivityEmailStore.update((s) => {
                s.record.emailSubject = response.data.emailSubject;
            });
            LeadActivityEmailStore.update((s) => {
                s.record.userCustomerDetailsId = response.data.userCustomerDetailsId;
            });

            setHasLoaded(true);

            if (response.data.hasEmailFromError) {
                alert('Need to handle email error here');
            }
        };
        if (selectedLead != null) fetchOutcomes();
    }, []);

    const handleSubjectTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        LeadActivityOutcomeStore.update((s) => {
            s.record.emailSubject = event.target.value;
        });
        LeadActivityEmailStore.update((s) => {
            s.record.emailSubject = event.target.value;
        });
    };

    const handleBodyTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        LeadActivityOutcomeStore.update((s) => {
            s.record.emailBody = event.target.value;
        });
        LeadActivityEmailStore.update((s) => {
            s.record.emailBody = event.target.value;
        });
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailTo(event.target.value);
        LeadActivityOutcomeStore.update((s) => {
            s.record.emailTo = event.target.value;
        });
    };

    useEffect(() => {
        const newConnection = new HubConnectionBuilder().withUrl('/hubs/twilio').withAutomaticReconnect().build();

        setConnection(newConnection);
    }, []);

    useEffect(() => {
        if (connection) {
            connection
                .start()
                .then((result) => {
                    console.log('Connected!');

                    connection.on('UpdateStatus', (message) => {
                        const updatedStatus = { ...message };
                        setStatus(updatedStatus);
                    });
                })
                .catch((e) => console.log('Connection failed: ', e));
        }
    }, [connection]);

    useEffect(() => {
        if (messageId != '' && status.messageId.toLowerCase() == messageId.toLowerCase()) {
            setSentStatus('Message ' + status.status);
            setShowMessageStatus(true);
        }
    }, [status]);

    const sendEmail = async () => {
        let hasError = false;
        if (_.isEmpty(outcomesRecord.emailBody)) {
            setSmsBodyError(true);
            hasError = true;
        }

        if (hasError) return;

        setIsSending(true);
        setSendButtonDisabled(true);
        setSentStatus('Sending, please wait...');
        setShowMessageStatus(true);

        await client
            .post(leadEndpoints.sendemailmessage, emailMessage)
            .then(async (response) => {
                setMessageId(response.data.messageId);
                LeadActivityOutcomeStore.update((s) => {
                    s.record.actionResult = response.data.status;
                });
                LeadActivityOutcomeStore.update((s) => {
                    s.record.actionId = response.data.messageId;
                });
                setIsSending(false);
                if (response.data.status == 'SUBMITTED') {
                    LeadActivityOutcomeStore.update((s) => {
                        s.record.saveDisabled = false;
                    });
                    setSendButtonDisabled(true);
                    setSentStatus('Email sent.');
                    setShowMessageStatus(true);
                    LeadActivityOutcomeStore.update((s) => {
                        s.record.selectedId = outcomesRecord.autoSentId;
                    }); //Sent correctly
                } else {
                    //Show error message
                    setSentStatus('ERROR: ' + response.data.errorMessage);
                    setShowMessageStatus(true);
                    LeadActivityOutcomeStore.update((s) => {
                        s.record.saveDisabled = false;
                    });
                    setSendButtonDisabled(true);
                    LeadActivityOutcomeStore.update((s) => {
                        s.record.selectedId = outcomesRecord.autoNotSentId;
                    }); //Did not send
                }
            })
            .catch((err: ProblemDetails) => {
                if (err.errors) {
                    for (const [key, value] of Object.entries(err.errors)) {
                        const errorMessage = '';

                        value.forEach((message) => {
                            message += message + '\\';
                        });
                    }
                }
            });
    };

    const viewActivityTemplates = () => {
        ActivityTemplateSelectionStore.update((s) => {
            s.activityTemplateSelectionMode = true;
        });
    };

    if (hasLoaded) {
        return (
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <Box>
                                    <Grid container alignContent="left" spacing={2}>
                                        <Grid item xs={12} sx={{ alignItems: 'right' }} justifyContent={'flex-end'}>
                                            <Typography sx={{ fontSize: 20, color: 'CornflowerBlue' }} gutterBottom>
                                                Outgoing Email
                                            </Typography>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={5}
                                            alignContent="left"
                                            justifyContent={'left'}
                                            sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                        >
                                            <FormControl fullWidth>
                                                <TextField
                                                    label="From"
                                                    id="email-from"
                                                    value={outcomesRecord.emailFrom}
                                                    size="small"
                                                    disabled
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={5}
                                            alignContent="left"
                                            justifyContent={'left'}
                                            sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                        >
                                            {!outcomesRecord.hasTwoEmails && (
                                                <FormControl fullWidth>
                                                    <TextField
                                                        label="To"
                                                        id="email-to"
                                                        value={outcomesRecord.emailTo}
                                                        size="small"
                                                        disabled
                                                    />
                                                </FormControl>
                                            )}
                                            {outcomesRecord.hasTwoEmails && (
                                                <FormControl>
                                                    <InputLabel id="demo-simple-select-label8">To</InputLabel>
                                                    <Select
                                                        labelId="demo-simple-select-label8"
                                                        id="demo-simple-select"
                                                        value={emailTo}
                                                        label="To"
                                                        onChange={handleEmailChange}
                                                        size="small"
                                                    >
                                                        <MenuItem value={defaultEmailTo}>{defaultEmailTo}</MenuItem>
                                                        <MenuItem value={outcomesRecord.switchboardEmail}>
                                                            {outcomesRecord.switchboardEmail}
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl>
                                            )}
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={2}
                                            alignItems="center"
                                            sx={{ pt: 1, display: 'flex', flexDirection: 'row' }}
                                        >
                                            {hasLoaded && !isSending && (
                                                <Tooltip title="Send email">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="info"
                                                        startIcon={<SendIcon />}
                                                        sx={{ mr: 3 }}
                                                        onClick={sendEmail}
                                                        disabled={sendButtonDisabled}
                                                    >
                                                        Send
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {hasLoaded && isSending && (
                                                <Tooltip title="Sending email">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="info"
                                                        startIcon={<SendIcon />}
                                                        sx={{ mr: 3 }}
                                                        disabled={true}
                                                    >
                                                        Sending
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </Grid>
                                        {showMessageStatus && (
                                            <Grid
                                                item
                                                xs={12}
                                                md={12}
                                                alignContent="left"
                                                justifyContent={'left'}
                                                sx={{
                                                    pt: 1,
                                                    display: 'flex',
                                                    alignItems: 'left',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <Alert severity="info">{sentStatus}</Alert>
                                            </Grid>
                                        )}
                                        <Grid
                                            item
                                            xs={12}
                                            md={12}
                                            alignContent="left"
                                            justifyContent={'left'}
                                            sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                        >
                                            <FormControl fullWidth>
                                                <TextField
                                                    label="Subject"
                                                    id="subject"
                                                    value={outcomesRecord.emailSubject}
                                                    size="small"
                                                    onChange={handleSubjectTextChange}
                                                    disabled={sendButtonDisabled}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={4}
                                            alignContent="left"
                                            justifyContent={'left'}
                                            sx={{ display: 'flex', alignItems: 'right', flexDirection: 'row' }}
                                        >
                                            <Link
                                                sx={{ fontStyle: 'italic' }}
                                                underline="hover"
                                                component="button"
                                                variant="caption"
                                                onClick={() => setSelected(!selected)}
                                            >
                                                {selected ? 'Hide toolbar' : 'Show toolbar'}
                                            </Link>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={4}
                                            alignContent="left"
                                            justifyContent={'left'}
                                            sx={{ display: 'flex', alignItems: 'right', flexDirection: 'row' }}
                                        >
                                            <LeadActivityAttachments />
                                        </Grid>
                                        <RenderIf claimType={ClaimType.Leads} claimValue={ClaimValue.TemplateManager}>
                                            <Grid
                                                item
                                                xs={12}
                                                md={4}
                                                alignContent="right"
                                                justifyContent="right"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'right',
                                                    flexDirection: 'row-reverse',
                                                }}
                                            >
                                                <Link
                                                    sx={{ fontStyle: 'italic' }}
                                                    underline="hover"
                                                    component="button"
                                                    variant="caption"
                                                    onClick={viewActivityTemplates}
                                                >
                                                    Templates
                                                </Link>
                                            </Grid>
                                        </RenderIf>
                                        <Grid
                                            item
                                            xs={12}
                                            md={12}
                                            alignContent="left"
                                            justifyContent={'left'}
                                            sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                        >
                                            {/*<FormControlLabel control={<Switch checked={showToolbar} onChange={handleShowToolbarChange} />} label="Show toolbar" />*/}
                                            {/*<Tooltip title={selected ? "Hide toolbar" : "Show toolbar" } >*/}
                                            {/*    <ToggleButton*/}
                                            {/*        fullWidth={false}*/}
                                            {/*        sx={{ width: 15, height: 15, mb: 1 }}*/}
                                            {/*        value="check"*/}
                                            {/*        size="small"*/}
                                            {/*        selected={selected}*/}
                                            {/*        onChange={() => {*/}
                                            {/*            setSelected(!selected);*/}
                                            {/*        }}*/}
                                            {/*    >*/}
                                            {/*        <MenuBookIcon />*/}
                                            {/*    </ToggleButton>*/}
                                            {/*</Tooltip>*/}
                                            <ContentEditor
                                                html={tokenizedHtml}
                                                disabled={sendButtonDisabled}
                                                onChange={setExample1EditorState}
                                                tokenType={TokenType.Lead}
                                                showToolbar={showToolbar}
                                            />
                                            {smsBodyError && (
                                                <Typography variant="caption" noWrap sx={{ color: 'red' }} align="left">
                                                    Email body is required!
                                                </Typography>
                                            )}
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={12}
                                            alignContent="left"
                                            justifyContent={'left'}
                                            sx={{ pt: 1, display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                        >
                                            <DoActivityOutcomes />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        );
    } else {
        return <StandardSkeleton />;
    }
};

export { DoActivityOutboundEmail };

export default DoActivityOutboundEmail;
